
//#region DefaultOperators

import { BooleanPropertyConfigTypes, DatedPropertyConfigTypes, NumericPropertyConfigTypes, PropertyConfigType } from "../../forms/propertyConfig.types";
import { arraysEqualIgnoreOrder, wrapAsArray } from "../../tools/collection.array.tools";
import { anyReallyAny, compareAsNumber } from "../../tools/object.tools";

/** Equal default operator */
export const equalOperator: AdvancedFilterOperator = {
    uid: "equalOperator",
    label: "=",
    tooltip: "Égal",// TODO i18n
    icon: "equals",

    /**
     * Comparator of equal operator
     * 
     * Compare as array is one of elements to compare is an array
     * * If value = [45] and valueToCompareArr = 45 => should return true
     * * If value = [45, 14] and valueToCompareArr = 45 => should return false
     */
    comparator: (value: anyReallyAny, valueToCompare: anyReallyAny): boolean => {

        // Compare all items in array are in other array
        if (Array.isArray(value) || Array.isArray(valueToCompare)) {

            // If value isn't an array, wrap it into array to apply compare
            let valueArr = value;
            if (!Array.isArray(valueArr)) { valueArr = wrapAsArray(valueArr); }


            // If valueToCompare isn't an array, wrap it into array to apply compare
            let valueToCompareArr = valueToCompare;
            if (!Array.isArray(valueToCompareArr)) { valueToCompareArr = wrapAsArray(valueToCompareArr); }

            // Always compare arrays values
            return arraysEqualIgnoreOrder(valueArr, valueToCompareArr);
        }

        return value === valueToCompare;
    }
};

/** Not Equal default operator */
export const notEqualOperator: AdvancedFilterOperator = {
    uid: "notEqualOperator",
    label: "!=",
    tooltip: "Différent de",// TODO i18n
    icon: "not-equal",
    /**
     * Comparator of not equal operator
     * 
     * Compare as array is one of elements to compare is an array
     * * If value = [45] and valueToCompareArr = 58 => should return true
     * * If value = [45, 26] and valueToCompareArr = 58 => should return false
     */
    comparator: (value: anyReallyAny, valueToCompare: anyReallyAny): boolean => {

        // Compare all items in array are in other array
        if (Array.isArray(value) || Array.isArray(valueToCompare)) {

            let valueArr = value;
            if (!Array.isArray(valueArr)) { valueArr = wrapAsArray(valueArr); }

            let valueToCompareArr = valueToCompare;
            if (!Array.isArray(valueToCompareArr)) { valueToCompareArr = wrapAsArray(valueToCompareArr); }

            return !arraysEqualIgnoreOrder(valueArr, valueToCompareArr);
        }

        return value !== valueToCompare;
    }
};

/** inferior default operator */
export const inferiorOperator: AdvancedFilterOperator = {
    uid: "inferiorOperator",
    label: "<",
    tooltip: "Inférieur à",// TODO i18n
    icon: "less-than",
    comparator: (value: unknown, valueToCompare: number): boolean => {
        if (value == null || valueToCompare == null) { return false; }
        return compareAsNumber(valueToCompare, value) > 0;
    },
    availableTypes: [...NumericPropertyConfigTypes, ...DatedPropertyConfigTypes]
};

/** superior default operator */
export const superiorOperator: AdvancedFilterOperator = {
    uid: "superiorOperator",
    label: ">",
    tooltip: "Supérieur à",// TODO i18n
    icon: "greater-than",
    comparator: (value: unknown, valueToCompare: number): boolean => {
        if (value == null || valueToCompare == null) { return false; }
        return compareAsNumber(valueToCompare, value) < 0;
    },
    availableTypes: [...NumericPropertyConfigTypes, ...DatedPropertyConfigTypes]
};

/** Contains default operator */
export const containsOperator: AdvancedFilterOperator = {
    uid: "containsOperator",
    label: "contient",// TODO i18n
    comparator: (value: unknown, valueToCompare: unknown): boolean => {
        const isValueArray = Array.isArray(value);
        const isValueToCompareArray = Array.isArray(valueToCompare);

        // If both values are array, check if all items of value are in valueToCompare
        if (isValueArray && isValueToCompareArray) {
            return value.every(val => valueToCompare.includes(val));
        }

        // If value is an array, check if at least one item is valueToCompare
        if (isValueArray) {
            return value.includes(valueToCompare);
        }

        // If valueToCompare is an array, check if valueToCompare contains value
        if (isValueToCompareArray) {
            return valueToCompare.includes(value);
        }

        const lowercase = String(value).toLowerCase();
        return lowercase.includes(String(valueToCompare).toLowerCase());
    },
    unavailableTypes: [...DatedPropertyConfigTypes, ...BooleanPropertyConfigTypes]
};

/** not Contains default operator */
export const notContainsOperator: AdvancedFilterOperator = {
    uid: "notContainsOperator",
    label: "ne contient pas",// TODO i18n
    comparator: (value: unknown, valueToCompare: unknown): boolean => {

        const isValueArray = Array.isArray(value);
        const isValueToCompareArray = Array.isArray(valueToCompare);

        // If both values are array, check if all items of value aren't in valueToCompare
        if (isValueArray && isValueToCompareArray) {
            return !value.every(val => valueToCompare.includes(val));
        }

        // If value is an array, check if value don't contains valueToCompare
        if (isValueArray) {
            return !value.includes(valueToCompare);
        }

        // If valueToCompare is an array, check if valueToCompare don't contains value
        if (isValueToCompareArray) {
            return !valueToCompare.includes(value);
        }

        const lowercase = String(value).toLowerCase();
        return !lowercase.includes(String(valueToCompare).toLowerCase());
    },
    unavailableTypes: [...DatedPropertyConfigTypes, ...BooleanPropertyConfigTypes]
};


/** Is empty default operator */
export const isEmptyOperator: AdvancedFilterOperator = {
    uid: "isEmptyOperator",
    label: "est vide",// TODO i18n
    comparator: (value: unknown, valueToCompare: unknown): boolean => {

        // Value is array, check if array is not empty
        if (Array.isArray(value)) {
            return value.length === 0;
        }

        return value == null || value === "";
    }
};


/** Is not empty default operator */
export const isNotEmptyOperator: AdvancedFilterOperator = {
    uid: "isNotEmptyOperator",
    label: "est non vide",// TODO i18n
    comparator: (value: unknown, valueToCompare: unknown): boolean => {

        // Value is array, check if array is not empty
        if (Array.isArray(value)) {
            return value.length > 0;
        }

        return value != null && value !== "";
    }
};

/** default operator list */
export const DEFAULT_ADVANCED_FILTER_OPERATOR_LIST = [
    equalOperator,
    notEqualOperator,
    inferiorOperator,
    superiorOperator,
    containsOperator,
    notContainsOperator,
    isEmptyOperator,
    isNotEmptyOperator
] as const;

//#endregion

//#region Interfaces (options etc)

export type OperatorUid = string;

/** Custom Operator */
export interface AdvancedFilterOperator {
    uid: OperatorUid;
    label: string;
    /** Title displayed in select */
    tooltip?: string;
    /** Icon of operator in select */
    icon?: string;// ex: fa-equals / fa-not-equals
    /** Compare function of the Comparator */
    comparator(value: unknown, valueToCompare: unknown): boolean;
    /** List of property config types compatible */
    availableTypes?: PropertyConfigType[];
    /** List of property config types not compatible */
    unavailableTypes?: PropertyConfigType[];
}

//#endregion